import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useFragment } from "react-relay";

import { AttendeeCircle } from "@whoosh/design-system/src/components/display";
import { HStack, Text } from "@whoosh/design-system/src/components/primitive";

import { groupSizeAtom } from "../../atoms";
import { NewEventDetailAttendeesGroupListItemFragment$key } from "./__generated__/NewEventDetailAttendeesGroupListItemFragment.graphql";

type NewEventDetailAttendeesGroupListItemProps = {
  fragmentRef: NewEventDetailAttendeesGroupListItemFragment$key | null;
  playerNounOverride?: string;
};

export const NewEventDetailAttendeesGroupListItem = ({
  fragmentRef,
  playerNounOverride,
}: NewEventDetailAttendeesGroupListItemProps) => {
  const data = useFragment(
    NewEventDetailAttendeesGroupListItemFragmentString,
    fragmentRef
  );

  const maxGroupSize = useAtomValue(groupSizeAtom);

  const registrations = data?.registrations?.edges || [];
  const numGuestRegistrations =
    registrations?.filter(
      (registration) => registration?.node?.type === "GUEST"
    ).length ?? 0;

  const numMemberRegistrations =
    registrations?.filter(
      (registration) => registration?.node?.type === "MEMBER"
    ).length ?? 0;

  const names = registrations
    .map((r) => {
      const registration = r?.node;
      const isRegistrantUnknownGuest =
        registration?.type === "GUEST" && !registration?.registrant?.firstName;
      const isRegistrantMemberHold =
        registration?.type === "MEMBER" && !registration?.registrant?.firstName;

      if (isRegistrantUnknownGuest) {
        return t`Unknown Guest`;
      } else if (isRegistrantMemberHold) {
        return t`Member Hold`;
      } else if (
        registration?.registrant?.firstName &&
        registration?.registrant?.lastName
      ) {
        return `${registration.registrant.firstName} ${registration.registrant.lastName}`;
      }
      return t`Unknown ${playerNounOverride ?? "Player"}`;
    })
    .filter(Boolean);

  return (
    <HStack alignItems="center" gap={4}>
      <AttendeeCircle
        maxSize={maxGroupSize}
        currentKnownClients={numMemberRegistrations}
        currentUnknownClients={numGuestRegistrations}
        isPublic={false}
      />
      <Text variant="mainBaseMedium" flex={1}>
        {names.join(", ")}
      </Text>
    </HStack>
  );
};

export const NewEventDetailAttendeesGroupListItemFragmentString = graphql`
  fragment NewEventDetailAttendeesGroupListItemFragment on EventSignUp {
    id
    registrations {
      edges {
        node {
          id
          type
          registrant {
            id
            firstName
            lastName
            profileImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
          }
        }
      }
    }
  }
`;
